import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/core/utils/utils";

interface ToggleSwitchContextType {
  value: string;
  onValueChange: (value: string) => void;
}

const ToggleSwitchContext = React.createContext<ToggleSwitchContextType | null>(
  null,
);

const useToggleSwitchContext = () => {
  const context = React.useContext(ToggleSwitchContext);
  if (!context) {
    throw new Error(
      "ToggleSwitch components must be used within ToggleSwitch.Root",
    );
  }
  return context;
};

const ToggleSwitchRoot = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    value: string;
    onValueChange: (value: string) => void;
  }
>(({ className, value, onValueChange, children, ...props }, ref) => {
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateIndicator = () => {
      const activeItem = containerRef.current?.querySelector(
        `[data-value="${value}"]`,
      );
      if (activeItem instanceof HTMLElement && containerRef.current) {
        setIndicator({
          left: activeItem.offsetLeft,
          width: activeItem.offsetWidth,
        });
      }
    };

    // Small delay to ensure DOM is rendered
    const timer = setTimeout(updateIndicator, 0);
    return () => clearTimeout(timer);
  }, [value, children]);

  return (
    <ToggleSwitchContext.Provider value={{ value, onValueChange }}>
      <div
        ref={containerRef}
        className={cn(
          "relative inline-flex rounded-full bg-card outline outline-2 outline-card",
          className,
        )}
        {...props}
      >
        <div
          className="absolute bottom-0 top-0 rounded-full bg-primary transition-all duration-300 ease-out"
          style={{
            left: indicator.left,
            width: indicator.width,
          }}
        />
        <div ref={ref} className="relative flex">
          {children}
        </div>
      </div>
    </ToggleSwitchContext.Provider>
  );
});
ToggleSwitchRoot.displayName = "ToggleSwitchRoot";

const ToggleSwitchItem = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    value: string;
    children: React.ReactNode;
  }
>(({ className, value, children, ...props }, ref) => {
  const { value: selectedValue, onValueChange } = useToggleSwitchContext();
  const isSelected = selectedValue === value;

  return (
    <button
      ref={ref}
      data-value={value}
      onClick={() => onValueChange(value)}
      className={cn(
        "relative z-10 flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        isSelected ? "text-primary-inverse-text" : "text-secondary-text",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});
ToggleSwitchItem.displayName = "ToggleSwitchItem";

export { ToggleSwitchRoot as Root, ToggleSwitchItem as Item };
